import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  IAuditoriaBeneficiosIndiretosResultado,
  IItemBeneficioIndireto,
  IQsaConsulta,
  IQsaSocio,
} from '../interfaces/IAuditoriaBeneficiosIndiretos';
import QsaGerenciarModal from './QsaGerenciarModal';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaBeneficiosIndiretosResultado;
  empresaId?: number;
  nomeEmpresa?: string;
  periodo?: string;
  onReprocessar?: () => void;
}

const rotuloTipoPessoa = (tipo?: string) => (tipo === 'PESSOA_JURIDICA' ? 'Pessoa Jurídica' : 'Pessoa Física');

// COR-234: colunas da exportação de lançamentos — CSV (";", compatível Excel pt-BR) e XLSX
// compartilham a mesma montagem de linhas.
interface LinhaExportacaoBeneficioIndireto {
  Empresa: string;
  CNPJ: string;
  'Período Auditado': string;
  'Data do Lançamento': string;
  'Nº Lançamento ECD': string;
  'Conta Contábil': string;
  'Descrição da Conta': string;
  'Referencial RFB': string;
  'Histórico': string;
  'Valor (R$)': string;
  'Débito/Crédito': string;
  'Sócio Identificado': string;
  'Tipo do Sócio': string;
  'Qualificação no QSA': string;
  'CPF/CNPJ Mascarado do Sócio': string;
  'Grau de Evidência': string;
  Classificação: string;
  'IRRF Estimado (R$)': string;
  'Multa ECD Estimada (R$)': string;
  'Impacto Fiscal Estimado (R$)': string;
  Diagnóstico: string;
  'Memória de Cálculo': string;
}

const montarLinhasExportacao = (
  itens: IItemBeneficioIndireto[],
  qsaConsultas: IQsaConsulta[],
  periodo: string,
  rotuloGrauEvidencia: (grau?: string) => string
): LinhaExportacaoBeneficioIndireto[] => {
  const cnpjPorEmpresa = new Map(qsaConsultas.map(q => [q.empresaId, q.cnpj]));
  const socioPorNome = new Map<string, IQsaSocio>();
  qsaConsultas.forEach(q => q.socios.forEach(s => socioPorNome.set(s.nome.trim().toUpperCase(), s)));

  return itens.map(item => {
    const socio = socioPorNome.get((item.nomeSocio || '').trim().toUpperCase());

    return {
      Empresa: item.empresaNome,
      CNPJ: cnpjPorEmpresa.get(item.empresaId) || '',
      'Período Auditado': periodo,
      'Data do Lançamento': formatDate(item.dataLancamento),
      'Nº Lançamento ECD': item.numeroLancamento || '',
      'Conta Contábil': item.contaContabil,
      'Descrição da Conta': item.descricaoConta,
      'Referencial RFB': item.contaReferencial,
      'Histórico': item.historico,
      'Valor (R$)': item.valor.toFixed(2).replace('.', ','),
      'Débito/Crédito': 'D',
      'Sócio Identificado': item.nomeSocio,
      'Tipo do Sócio': socio ? rotuloTipoPessoa(socio.tipoPessoa) : '',
      'Qualificação no QSA': socio?.qualificacao || '',
      'CPF/CNPJ Mascarado do Sócio': socio?.cpfCnpjMascarado || '',
      'Grau de Evidência': item.grauEvidencia ? rotuloGrauEvidencia(item.grauEvidencia) : '',
      Classificação: item.status,
      'IRRF Estimado (R$)': item.valorIrrf.toFixed(2).replace('.', ','),
      'Multa ECD Estimada (R$)': item.multaEcd.toFixed(2).replace('.', ','),
      'Impacto Fiscal Estimado (R$)': item.impactoFiscalTotal.toFixed(2).replace('.', ','),
      Diagnóstico: item.mensagemDiagnostico,
      'Memória de Cálculo': item.memoriaCalculo,
    };
  });
};

const exportarCSV = (linhas: LinhaExportacaoBeneficioIndireto[], nomeArquivo: string) => {
  if (linhas.length === 0) return;
  const cabecalho = Object.keys(linhas[0]);
  const corpo = linhas.map(linha => Object.values(linha));
  const csv = [cabecalho, ...corpo]
    .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'))
    .join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${nomeArquivo}.csv`);
};

const exportarXLSX = (linhas: LinhaExportacaoBeneficioIndireto[], nomeArquivo: string) => {
  if (linhas.length === 0) return;
  const ws = XLSX.utils.json_to_sheet(linhas);
  const wb = { Sheets: { Lançamentos: ws }, SheetNames: ['Lançamentos'] };
  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
  saveAs(blob, `${nomeArquivo}.xlsx`);
};



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    case 'PENDENTE':
      return 'bi-clock-history';
    default:
      return 'bi-check-circle-fill';
  }
};

const AuditoriaBeneficiosIndiretosDetalhe: React.FC<Props> = ({ resultado, empresaId, nomeEmpresa, periodo, onReprocessar }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);
  const [qsaModalAberto, setQsaModalAberto] = useState(false);
  const [socioQsaExpandido, setSocioQsaExpandido] = useState<string | null>(null);

  const despesasIndevidas = resultado.despesasIndevidas || [];
  const proLaboreConciliado = resultado.proLaboreConciliado || [];
  const pendenciasExecucao = resultado.pendenciasExecucao || [];
  const qsaConsultas = resultado.qsaConsultas || [];

  // COR-134: a varredura por histórico genérico (sem sócio identificável) não depende do QSA —
  // separamos os dois critérios para exibir a seção do QSA em cima (pode ficar "pendente") e a
  // seção independente do QSA embaixo, sempre visível.
  // COR-230/232: "Transação com Parte Relacionada / Operação Comercial" (venda a sócio com receita
  // e baixa de estoque na mesma operação) é uma categoria própria, separada de Benefício Indireto
  // (confirmado/provável/indício fraco) — nunca deve aparecer misturada na mesma tabela/contagem
  // de risco fiscal, para não sugerir que uma venda comercial é uma despesa indevida.
  const comSocioIdentificado = despesasIndevidas.filter(item => item.nomeSocio !== '(não identificado)');
  const despesasComSocio = comSocioIdentificado.filter(item => item.grauEvidencia !== 'OPERACAO_COMERCIAL');
  const transacoesComerciais = comSocioIdentificado.filter(item => item.grauEvidencia === 'OPERACAO_COMERCIAL');
  const despesasSemIdentificacao = despesasIndevidas.filter(item => item.nomeSocio === '(não identificado)');

  const rotuloGrauEvidencia = (grau?: string) => {
    switch (grau) {
      case 'CONFIRMADO': return 'Confirmado';
      case 'PENDENTE_VALIDACAO': return 'Provável';
      case 'OPERACAO_COMERCIAL': return 'Operação Comercial com Sócio';
      default: return 'Indício Fraco';
    }
  };

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  const alternarSocioQsa = (chave: string) => {
    setSocioQsaExpandido(socioQsaExpandido === chave ? null : chave);
  };

  // COR-234: "Tudo" cobre todos os lançamentos que passaram pela auditoria (achados de Benefício
  // Indireto/Operação Comercial/histórico genérico + Pró-Labore conciliado) — os demais botões
  // filtram por Status, reaproveitando os mesmos dados já carregados na tela.
  const todosOsLancamentos = [...despesasIndevidas, ...proLaboreConciliado];
  const nomeBase = `beneficios_indiretos_${(nomeEmpresa || 'empresa').replace(/[^\w-]+/g, '_')}`;

  const exportar = (formato: 'csv' | 'xlsx', filtroStatus?: string) => {
    const itens = filtroStatus ? todosOsLancamentos.filter(i => i.status === filtroStatus) : todosOsLancamentos;
    const linhas = montarLinhasExportacao(itens, qsaConsultas, periodo || '', rotuloGrauEvidencia);
    const sufixo = filtroStatus ? `_${filtroStatus.toLowerCase()}` : '_tudo';
    if (formato === 'csv') exportarCSV(linhas, `${nomeBase}${sufixo}`);
    else exportarXLSX(linhas, `${nomeBase}${sufixo}`);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Benefícios Indiretos a Sócios — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      {/* COR-233: "QSA consultado na Receita Federal" — visibilidade/rastreabilidade da base
          societária usada no cruzamento, antes da lista de ocorrências. Aparece mesmo quando a
          consulta falhou (status da consulta fica visível, em vez de a tela ficar silenciosa). */}
      {qsaConsultas.length > 0 && (
        <div className='auditoria-card'>
          <h3>QSA Consultado na Receita Federal</h3>
          <p className='text-muted'>
            Sócios, administradores e representantes legais retornados pela integração com a Receita Federal
            (BrasilAPI/ReceitaWS) para esta empresa, usados como base do cruzamento com os históricos da ECD.
          </p>

          {qsaConsultas.map((consulta, idxConsulta) => (
            <div key={`qsa-${consulta.empresaId}-${idxConsulta}`} className='table-responsive mt-4'>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 8, fontSize: 12, color: '#374151' }}>
                <div><strong>CNPJ consultado:</strong> {consulta.cnpj}</div>
                <div><strong>Origem:</strong> {consulta.fonteConsulta || '—'}</div>
                <div><strong>Consulta em:</strong> {consulta.dataHoraConsulta ? new Date(consulta.dataHoraConsulta).toLocaleString('pt-BR') : '—'}</div>
                <div>
                  <strong>Status:</strong>{' '}
                  <span className={`auditoria-status status-${consulta.sucesso ? 'CONCILIADO' : 'AVISO'}`}>
                    {consulta.sucesso ? 'Sucesso' : 'Falha/Indisponível'}
                  </span>
                </div>
              </div>
              {!consulta.sucesso && (
                <div className='text-muted mb-2' style={{ fontSize: 12 }}>{consulta.mensagemAmigavel}</div>
              )}

              {consulta.socios.length > 0 ? (
                <table className='auditoria-table'>
                  <thead>
                    <tr>
                      <th>Nome / Razão Social</th>
                      <th>Tipo</th>
                      <th>Qualificação no QSA</th>
                      <th>CPF/CNPJ</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {consulta.socios.map((socio, idxSocio) => {
                      const chave = `qsa-socio-${consulta.empresaId}-${idxSocio}`;
                      const expandido = socioQsaExpandido === chave;
                      return (
                        <React.Fragment key={chave}>
                          <tr className='auditoria-row-tributo' onClick={() => alternarSocioQsa(chave)}>
                            <td><strong>{socio.nome}</strong></td>
                            <td>{rotuloTipoPessoa(socio.tipoPessoa)}</td>
                            <td>{socio.qualificacao || '—'}</td>
                            <td>{socio.cpfCnpjMascarado || <span className='text-muted'>não informado pela fonte</span>}</td>
                            <td>
                              <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`} />
                            </td>
                          </tr>
                          {expandido && (
                            <tr>
                              <td colSpan={5} style={{ background: '#fafbfc' }}>
                                <div className='py-2 px-3'>
                                  <strong>Critério de matching usado no cruzamento com a ECD:</strong>{' '}
                                  {socio.tokensCruzamento.length > 0
                                    ? socio.tokensCruzamento.map(t => (
                                        <span key={t} className='auditoria-status status-AVISO' style={{ marginRight: 6 }}>{t}</span>
                                      ))
                                    : <span className='text-muted'>nenhum termo distintivo — não entra na varredura por nome</span>}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className='text-muted' style={{ fontSize: 12 }}>Nenhum sócio retornado pela consulta.</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* COR-234: exportação dos lançamentos identificados — CSV/XLSX, com os dados do QSA usados
          no cruzamento incluídos em cada linha. */}
      {todosOsLancamentos.length > 0 && (
        <div className='auditoria-card' style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <strong style={{ marginRight: 4 }}>
            <i className='bi bi-download me-1' />
            Exportar lançamentos:
          </strong>
          <button type='button' className='btn btn-sm btn-outline-primary' onClick={() => exportar('csv')}>
            Tudo (CSV) — {todosOsLancamentos.length}
          </button>
          <button type='button' className='btn btn-sm btn-outline-primary' onClick={() => exportar('xlsx')}>
            Tudo (XLSX)
          </button>
          {resultado.totalErros > 0 && (
            <button type='button' className='btn btn-sm btn-outline-danger' onClick={() => exportar('csv', 'ERRO')}>
              Erros (CSV) — {resultado.totalErros}
            </button>
          )}
          {resultado.totalAlertas > 0 && (
            <button type='button' className='btn btn-sm btn-outline-warning' onClick={() => exportar('csv', 'ALERTA')}>
              Alertas (CSV) — {resultado.totalAlertas}
            </button>
          )}
          {resultado.totalAvisos > 0 && (
            <button type='button' className='btn btn-sm btn-outline-secondary' onClick={() => exportar('csv', 'AVISO')}>
              Avisos (CSV) — {resultado.totalAvisos}
            </button>
          )}
        </div>
      )}

      {!resultado.qsaDisponivel && (
        <div className='auditoria-card' style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <i className='bi bi-exclamation-circle-fill text-warning' style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <strong>QSA indisponível</strong>
                <div style={{ marginTop: 4, color: '#374151' }}>{resultado.mensagemQsa}</div>
                {resultado.mensagemQsaTecnica && (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: 'pointer', color: '#6b7280', fontSize: 12, userSelect: 'none' }}>
                      <i className='bi bi-bug me-1' />
                      Detalhe técnico
                    </summary>
                    <div style={{ marginTop: 6, fontSize: 12, color: '#9ca3af', fontFamily: 'monospace', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px' }}>
                      {resultado.mensagemQsaTecnica}
                    </div>
                  </details>
                )}
              </div>
            </div>
          </div>
          {empresaId && (
            <button
              type='button'
              className='btn btn-sm btn-outline-primary'
              style={{ flexShrink: 0 }}
              onClick={() => setQsaModalAberto(true)}
            >
              <i className='bi bi-people-fill me-2' />
              Gerenciar QSA
            </button>
          )}
        </div>
      )}

      {qsaModalAberto && empresaId && (
        <QsaGerenciarModal
          empresaId={empresaId}
          nomeEmpresa={nomeEmpresa}
          onFechar={() => setQsaModalAberto(false)}
          onQsaAtualizado={() => {
            setQsaModalAberto(false);
            if (onReprocessar) onReprocessar();
          }}
        />
      )}

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 && !resultado.qsaDisponivel ? (
            // COR-76: zerado por falta de QSA — deixar explícito que não é "sem risco"
            <div className='auditoria-kpi-help' style={{ color: '#92400e', fontSize: 11, marginTop: 4, fontStyle: 'italic' }}>
              Zerado por ausência de QSA, não por ausência de risco.
            </div>
          ) : resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>IRRF exclusivo na fonte 35% (Art. 74 Lei 8.383/1991) + Multa ECD potencial (5% limitada a 1% da receita bruta do período — Art. 12, II, Lei 8.218/1991).</div>
          )}
        </div>

        {/* COR-134: totais agora combinam achados por sócio (QSA) e por histórico genérico
            (independe do QSA) — por isso o card de contadores aparece sempre, mesmo com QSA
            indisponível, quando a varredura por histórico genérico já encontrou algo. */}
        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>
            {resultado.qsaDisponivel
              ? 'Despesas com indício de Benefício Indireto a sócio(s) do QSA e por histórico genérico.'
              : 'Despesas com histórico genérico (independe do QSA) — varredura por nome de sócio ainda pendente.'}
          </div>
        </div>

        {!resultado.qsaDisponivel && (
          <div className='auditoria-kpi'>
            <div className='auditoria-kpi-label'>Pendências de Execução</div>
            <div className='auditoria-kpi-value'>
              <span className='auditoria-contador auditoria-contador-aviso'>
                {pendenciasExecucao.length || 1}
              </span>
            </div>
            <div className='auditoria-kpi-help'>
              QSA indisponível — a varredura por nome de sócio não foi executada.
            </div>
          </div>
        )}

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal aplicada ao Benefício Indireto.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Benefício Indireto:</strong> {resultado.fundamentacaoBeneficioIndireto}</div>
        </div>
      )}

      {/* COR-76: card condicional — varredura pendente vs. achados de auditoria */}
      {!resultado.qsaDisponivel ? (
        <div className='auditoria-card' style={{ borderLeft: '4px solid #c7d2fe' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <i className='bi bi-hourglass-split' style={{ fontSize: 22, color: '#3730a3', flexShrink: 0, marginTop: 3 }} />
            <div style={{ flex: 1 }}>
              <strong style={{ color: '#1f2a44', fontSize: 15 }}>Varredura pendente de base de sócios</strong>
              <p style={{ marginTop: 6, marginBottom: 12, color: '#374151', fontSize: 13, lineHeight: 1.6 }}>
                A auditoria de Benefício Indireto requer o Quadro de Sócios e Administradores (QSA) para
                cruzar históricos de lançamento com nomes de sócios. Sem essa base, nenhuma varredura foi
                executada.{' '}
                <strong>R$&nbsp;0,00 de impacto fiscal não significa ausência de risco</strong>&nbsp;—
                significa que a análise está incompleta enquanto o QSA não for obtido.
              </p>

              <div style={{ fontSize: 12, lineHeight: 2 }}>
                <div style={{ color: '#126b3f' }}>
                  <i className='bi bi-check-circle-fill me-2' />1. Validação do CNPJ
                </div>
                <div style={{ color: '#d97706' }}>
                  <i className='bi bi-exclamation-circle-fill me-2' />2. Obtenção do QSA&nbsp;
                  <em style={{ fontWeight: 400 }}>— pendente</em>
                </div>
                <div style={{ color: '#9ca3af' }}>
                  <i className='bi bi-circle me-2' />3. Confirmação da base de sócios/administradores
                </div>
                <div style={{ color: '#9ca3af' }}>
                  <i className='bi bi-circle me-2' />4. Varredura em contas de despesa (exceto Pró-Labore)
                </div>
                <div style={{ color: '#9ca3af' }}>
                  <i className='bi bi-circle me-2' />5. Identificação de despesas com indício de Benefício Indireto
                </div>
                <div style={{ color: '#9ca3af' }}>
                  <i className='bi bi-circle me-2' />6. Cálculo de impacto fiscal (IRRF 35% + Multa ECD Potencial)
                </div>
              </div>

              {onReprocessar && (
                <div style={{ marginTop: 14 }}>
                  <button type='button' className='btn btn-sm btn-outline-primary' onClick={onReprocessar}>
                    <i className='bi bi-arrow-clockwise me-2' />
                    Reprocessar após obtenção do QSA
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
      <div className='auditoria-card'>
        <h3>Despesas com Indício de Benefício Indireto a Sócio Identificado (QSA)</h3>
        <p className='text-muted'>
          Lançamentos em contas de despesa (exceto Pró-Labore) cujo histórico cita o nome de um sócio do
          QSA. Caracteriza Remuneração Indireta, sujeita a IRRF exclusivo na fonte e Multa ECD.
        </p>

        {despesasComSocio.length === 0 && (
          <div className='text-muted mt-4'>
            <i className='bi bi-check-circle-fill text-success me-2' />
            Varredura executada — nenhuma despesa com indício de benefício indireto a sócio identificada no período.
          </div>
        )}

        {despesasComSocio.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Sócio</th>
                  <th>Data</th>
                  <th className='text-end'>Valor</th>
                  <th className='text-end'>IRRF (35%)</th>
                  <th className='text-end'>Multa ECD Potencial</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {despesasComSocio.map((item: IItemBeneficioIndireto, idx) => {
                  const chave = `bi-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.nomeSocio}</td>
                        <td>{formatDate(item.dataLancamento)}</td>
                        <td className='text-end'>{formatCurrency(item.valor)}</td>
                        <td className='text-end'>{formatCurrency(item.valorIrrf)}</td>
                        <td className='text-end'>{formatCurrency(item.multaEcd)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          {/* COR-77/232: grau de evidência abaixo do status */}
                          {item.grauEvidencia && item.grauEvidencia !== 'CONFIRMADO' && (
                            <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                              {rotuloGrauEvidencia(item.grauEvidencia)}
                            </div>
                          )}
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Histórico:</strong> {item.historico}</div>
                              {/* COR-77/232: grau de evidência explícito na expansão */}
                              {item.grauEvidencia && (
                                <div>
                                  <strong>Grau de Evidência:</strong>{' '}
                                  <span style={{
                                    fontWeight: 600,
                                    color: item.grauEvidencia === 'CONFIRMADO' ? '#b91c1c'
                                         : item.grauEvidencia === 'PENDENTE_VALIDACAO' ? '#b45309'
                                         : '#6b7280',
                                  }}>
                                    {rotuloGrauEvidencia(item.grauEvidencia)}
                                  </span>
                                </div>
                              )}
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* COR-230/232/253: Transação com Parte Relacionada / Operação Comercial — venda a sócio com
          receita reconhecida e baixa de estoque na mesma operação contábil. Categoria própria,
          sem cálculo de risco fiscal, separada de Benefício Indireto para não distorcer o laudo.
          COR-253: ampliado para também cobrir as contas de tributo sobre vendas/faturamento
          (PIS/COFINS/ICMS/ISS) da mesma operação — não só a linha de Custo de Revenda. */}
      {resultado.qsaDisponivel && transacoesComerciais.length > 0 && (
        <div className='auditoria-card' style={{ borderLeft: '4px solid #93c5fd' }}>
          <h3>Transações Comerciais com Sócio (Parte Relacionada)</h3>
          <p className='text-muted'>
            Lançamentos em conta de Custo de Mercadoria Vendida/Revenda ou de tributo sobre vendas/faturamento
            (PIS/COFINS/ICMS/ISS) que citam o nome de um sócio, mas fazem parte de uma operação de venda (mesmo
            número de lançamento com receita reconhecida e baixa de estoque) — não configuram Benefício Indireto.
            Recomenda-se validar documentação da venda (nota fiscal, preço praticado, condições de recebimento).
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Sócio</th>
                  <th>Data</th>
                  <th className='text-end'>Valor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {transacoesComerciais.map((item: IItemBeneficioIndireto, idx) => {
                  const chave = `bi-comercial-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.nomeSocio}</td>
                        <td>{formatDate(item.dataLancamento)}</td>
                        <td className='text-end'>{formatCurrency(item.valor)}</td>
                        <td>
                          <span className='auditoria-status status-AVISO'>Operação Comercial</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={5} style={{ background: '#eff6ff' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Histórico:</strong> {item.historico}</div>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo (contrapartidas da operação):</strong> {item.memoriaCalculo}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* COR-134: histórico genérico/sem identificação — não depende do QSA, roda sempre.
          Fica sempre visível abaixo da seção do QSA, com ou sem sócio identificado. */}
      <div className='auditoria-card'>
        <h3>Despesas com Histórico Sem Identificação (independe do QSA)</h3>
        <p className='text-muted'>
          Lançamentos em contas de despesa (exceto Pró-Labore) cujo histórico não cita CPF/CNPJ, palavra
          estrutural reconhecida nem tem identificação textual suficiente — possível Benefício Indireto não
          confirmável pelo nome do sócio. Essa varredura não depende do QSA, então roda mesmo quando o CNPJ
          da empresa é inválido ou a fonte pública está indisponível.
        </p>

        {despesasSemIdentificacao.length === 0 && (
          <div className='text-muted mt-4'>
            <i className='bi bi-check-circle-fill text-success me-2' />
            Varredura executada — nenhum lançamento com histórico sem identificação encontrado no período.
          </div>
        )}

        {despesasSemIdentificacao.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Data</th>
                  <th className='text-end'>Valor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {despesasSemIdentificacao.map((item: IItemBeneficioIndireto, idx) => {
                  const chave = `bi-generico-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{formatDate(item.dataLancamento)}</td>
                        <td className='text-end'>{formatCurrency(item.valor)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={4} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Histórico:</strong> {item.historico}</div>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pró-Labore conciliado — visibilidade de que a auditoria rodou */}
      {proLaboreConciliado.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Lançamentos em Pró-Labore Verificados e Conciliados
          </h3>
          <p className='text-muted'>
            Lançamentos que citam o nome de um sócio do QSA, mas estão corretamente classificados na
            própria conta de Pró-Labore — remuneração formal, já tributada na folha.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Sócio</th>
                  <th>Data</th>
                  <th className='text-end'>Valor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {proLaboreConciliado.map((item: IItemBeneficioIndireto, idx) => {
                  const chave = `pl-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.nomeSocio}</td>
                        <td>{formatDate(item.dataLancamento)}</td>
                        <td className='text-end'>{formatCurrency(item.valor)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={5} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <i className='bi bi-check-circle-fill text-success me-1' />
                              <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaBeneficiosIndiretosDetalhe;
